/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react'
import { withRouter } from 'react-router-dom'
import { connect, useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import { get, groupBy, isEmpty, map, filter, values } from 'lodash'

import { isRunningOnAndroid, isHandheldDevice } from '../VNAndroidSDK/Utils'

import { createConfig, updateConfig } from '@ordernext/networking-stadium/stadium'

import StadiumRemote from '../remote/stadiumRemote'
import { BRANDING_COLOR } from '../constants'

import { ReactComponent as UnselectedRadioButton } from '../assets/icons/unselected-radio-button-icon.svg'
import { ReactComponent as SelectedRadioButton } from '../assets/icons/selected-radio-button-icon.svg'
import { ReactComponent as UnselectedCheckBox } from '../assets/icons/fill-unchecked.svg'
import { ReactComponent as SelectedCheckBox } from '../assets/icons/fill-checkmark.svg'

import usePrevious from '../hooks/usePrevious'
import useGetIsMobileScreen from '../hooks/useGetIsMobileScreen'

import { clearCart } from '../actions/cart'
import { currentMenuSelected, menusSucceeded } from '../actions/menus'
import { clearPromotionsOnOrder } from '../actions/promotion'
import { fetchAttendants } from '../actions/attendant'
import { fetchPrinters } from '../actions/printer'

import {
  getCharityDescription,
  getCharityImage,
  getCharityName,
  getCharityOptions,
  getCharityOptionsEnabled,
  getIsKiosk,
  getPosQrReceiptsEnabled,
  getRoundUpForCharityEnabled,
  getTextReceiptsEnabled,
} from '../selectors/menus'
import { getDeviceSubtype, getSelectedMenusIds } from '../selectors/config'
import { getAttendants, getFetchAttendantsStatus } from '../selectors/attendant'
import { getBuildInfoReceived, getPaymentType, getPosDeviceType, getTipOnCardReader } from '../selectors/peripheral'
import { getCurrentConfiguration } from '../selectors/config'
import { getNetworkPrinter, calculatePrintingEnabled, getNetworkPrinters, getPrinterRequestStatus } from '../selectors/printer'

import { POS_DEVICE_TYPES, getAccessToken, getDeviceId, getDeviceSerial } from '../utils/deviceData'
import DeviceSubtypes, { CFD, CONCESSIONS, KIOSK, DEVICE_TYPES } from '../utils/deviceSubtypes'
import { getNetworkAvailableValue } from '../utils/networkConnected'
import { DEFAULT_PRINTERS } from '../utils/printing'

import styles from './RevenueCenterSetup.module.scss'

import { sendInitializeCFD } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { setDeviceMode } from '../VNMode/ActionCreators'
import { MODES } from '../VNMode/Reducer'
import { getDeviceMode } from '../VNMode/Selectors'
import { setKey, setCluster } from '../VNPusher/ActionCreators'

import Button from '../components/Button'
import VNConcessionsNavBar from '../components/VNConcessionsNavBar'
import VNLoader from '../VNLoader/components/VNLoader'
import Record from '../components/Record'

import { disableBackbarMode, disableKioskBarcodeScanning, enableBackbarMode } from '../actions/config'
import { currentNetworkPrinterSelected } from '../actions/printer'
import ConfirmModalV2 from '../components/ConfirmModalV2'
import { setTipOnCardReader } from '../actions/peripheral'
import useGetEnvVars from '../hooks/useGetEnvVars'
import PorticoRemote from '../remote/porticoRemote'
import createDataDogLog from '../utils/datadog'
import { executeDeviceSubtype } from '../utils/bridgeMethods'
import { setRoute } from '../VNRoutes/ActionCreators'
import { SHIFT4_BLUE } from '../styles/constants'
import { resetBaseLoad } from '../VNDataLoadManager/ActionCreators'
import { invoiceNumberPrefix } from '../utils/orderNumbers'
import { clearImageCache } from '../VNImageCache/utils'
import { getDefaultTipEnabled } from '../selectors/revenueCenter'
import { getQrReceiptsUrl } from '../selectors/menus'

const menuTypes = {
  'concessions-pos': 'pos',
  kiosk: KIOSK,
}

const RevenueCenterSetup = ({
  history,
  loading,
  failed,
  hasConfig,
  currentDeviceSubtype,
  currentNetworkPrinters,
  createConfig,
  updateConfig,
  className,
  setCurrentMenuId,
  setCurrentNetworkPrinterUuid,
  setPusherKey,
  setPusherCluster,
  redirectToPin,
  onMount,
  attendantsExist,
  fetchAttendantsStatus,
  isKiosk,
  menuLoadSucceeded,
  paymentType,
  accessToken,
  configurationUuid,
  previousSelectedIds,
  hasBuildInfo,
  currentBackbarMode,
  enableBackbarMode,
  disableBackbarMode,
  networkPrinters,
  printerRequestStatus,
  loadPrinters,
  printerSetup,
  fromSelfCheckoutConfig,
  fromPrinterSettings,
  fromBarcodeScanningConfig,
}) => {
  const dispatch = useDispatch()

  const posDeviceType = useSelector(getPosDeviceType)
  const tipOnCardReader = useSelector(getTipOnCardReader)

  const classNames = cn(styles.revenueCenterSetup, className)
  const [selectedMenuUuids, setSelectedMenuUuids] = useState([])
  const [submitted, setSubmitted] = useState(false)
  const [menuRequestStatus, setMenuRequestStatus] = useState('loading')
  const [menuData, setMenuData] = useState({})
  const [deviceSubtype, setDeviceSubtype] = useState(currentDeviceSubtype)
  const [deviceSubtypeSelected, setDeviceSubtypeSelected] = useState(!!deviceSubtype)
  const [menuSelected, setMenuSelected] = useState(false)

  const [closeTabMethodSelected, setCloseTabMethodSelected] = useState(false)
  const [backbarMode, setBackbarMode] = useState(currentBackbarMode)
  const [showNoPrinterModal, setShowNoPrinterModal] = useState(false)

  const isMobileViewPort = useGetIsMobileScreen()

  const scaleSvgIcon = (icon) => {
    return <div className={styles.svg}>{icon}</div>
  }

  const deviceMode = useSelector((state) => getDeviceMode(state))
  const isCFDCapableDevice = isHandheldDevice()

  const unselectedIcon = DeviceSubtypes.isConcessions(deviceSubtype) ? <UnselectedRadioButton /> : <UnselectedCheckBox />
  const selectedIcon = DeviceSubtypes.isConcessions(deviceSubtype) ? <SelectedRadioButton /> : <SelectedCheckBox />
  const networkAvailable = useSelector((state) => getNetworkAvailableValue(state))
  const textReceiptsEnabled = useSelector(getTextReceiptsEnabled)
  const emailReceiptsEnabled = useSelector(getTextReceiptsEnabled)
  const posQrCodeReceiptsEnabled = useSelector(getPosQrReceiptsEnabled)
  const qrReceiptsUrl = useSelector(getQrReceiptsUrl)
  const roundUpForCharityEnabled = useSelector(getRoundUpForCharityEnabled)
  const charityOptionsEnabled = useSelector(getCharityOptionsEnabled)
  const charityDescription = useSelector(getCharityDescription)
  const defaultTipEnabled = useSelector(getDefaultTipEnabled)
  const charityOptions = useSelector(getCharityOptions)
  const charityImage = useSelector(getCharityImage)
  const charityName = useSelector(getCharityName)

  const stands = get(menuData, 'stands', [])
  const menus = get(menuData, 'menus', [])
  const filteredMenus = menus
    .filter((menu) => menu?.serviceType?.toLowerCase() === menuTypes[deviceSubtype])
    .sort((a, b) => (a.standName > b.standName ? 1 : -1))
  const groupedMenus = groupBy(filteredMenus, 'standUuid')
  const previousHasConfig = usePrevious(hasConfig)
  const attendantsFetchCompleted = ['succeeded', 'failed'].includes(fetchAttendantsStatus)

  const selectedMenu = menus.find((menu) => menu.uuid === selectedMenuUuids[0])
  const tipsEnabled = !isEmpty(selectedMenu?.tipSuggestions || [])
  const { isBackBarEnabled } = useGetEnvVars()
  const tabsModeEnabled = selectedMenu?.enableFullServiceInPos ?? false
  const canEnableBackbarMode = tabsModeEnabled && isBackBarEnabled() && deviceMode !== MODES.POS

  const getMenuHeader = () => {
    if (!deviceSubtypeSelected) return 'Select Device Type'
    if (!menuSelected) return 'Select Menu'
    if (!closeTabMethodSelected) return 'Select Close Tab Method'
    return 'Select Receipt Printer'
  }

  const menuHeader = getMenuHeader()

  const getNoTipsMessage = () => {
    if (deviceSubtypeSelected && menuSelected && !closeTabMethodSelected && !tipsEnabled) {
      return (
        <div className={cn([displayHeader && styles.noTips, !displayHeader && styles.noTipsMobile])}>
          {`${selectedMenu?.name} does not have tips enabled`}
        </div>
      )
    }
    return null
  }

  const [selectedNetworkPrinterUuids, setSelectedNetworkPrinterUuids] = useState(currentNetworkPrinters)
  const defaultPrinterRecords = values(DEFAULT_PRINTERS)

  const onSelectNetworkPrinter = (printerUuid) => {
    setSelectedNetworkPrinterUuids([printerUuid])
  }

  /**
   * To be able to enable Kiosk barcode scanning we need the device to be configured as Kiosk
   * that the feature flag is on and that the user selected only one menu
   */
  const canEnableKioskBarcodeScanning = useMemo(() => {
    if (deviceSubtype !== KIOSK) return false
    if (selectedMenuUuids.length > 1) return false

    return true
  }, [isKiosk, selectedMenuUuids])

  const doneButtonText = useMemo(() => {
    if (canEnableKioskBarcodeScanning) return 'Next'
    if (!isKiosk) return 'Next'
    return 'Done'
  }, [canEnableKioskBarcodeScanning, selectedNetworkPrinterUuids, isKiosk])

  const fromPrintingConfigLoading = useMemo(
    () =>
      (fromSelfCheckoutConfig || fromPrinterSettings || fromBarcodeScanningConfig) &&
      (menuRequestStatus === 'loading' || printerRequestStatus === 'loading'),
    [fromSelfCheckoutConfig, fromPrinterSettings, fromBarcodeScanningConfig, menuRequestStatus, printerRequestStatus],
  )

  const handleNavigation = () => {
    if (isKiosk) {
      if (canEnableKioskBarcodeScanning) {
        dispatch(setRoute('/self-checkout-config'))
        return
      }
      clearImageCache(null, true).finally(() => {
        history.replace('/kiosk/attract_loop')
      })
      return
    }

    if (!isKiosk && selectedNetworkPrinterUuids[0] !== 'none') {
      history.replace('/printer-settings-config')
      return
    }

    if (!isKiosk && selectedNetworkPrinterUuids[0] === 'none') {
      history.replace('/barcode-scanning-settings-config', { fromRevenueCenterSetup: true })
      return
    }

    if (attendantsExist) {
      redirectToPin(fetchAttendantsStatus)
      return
    }

    history.replace('/concession-order')
  }

  useEffect(() => {
    const hasConfigChanged = previousHasConfig === false && hasConfig === true
    const isValidConfigMode = DEVICE_TYPES.includes(currentDeviceSubtype)

    if (hasConfigChanged && isValidConfigMode && attendantsFetchCompleted) {
      handleNavigation()
    }
  }, [hasConfig, fetchAttendantsStatus])

  const refreshPusherInformation = () => {
    PorticoRemote.getPusherInfo()
      .then((res) => {
        const pusherKey = res.data.key
        const pusherCluster = res.data.cluster

        setPusherKey(pusherKey)
        setPusherCluster(pusherCluster)
      })
      .catch((err) => {
        createDataDogLog('error', 'Failed to fetch pusher info', err)
      })
  }

  //TODO (JEFF) Possibly refactor this screen to pull menu data from redux
  const fetchMenus = () => {
    setMenuRequestStatus('loading')
    StadiumRemote.fetchMenus({ paymentType })
      .then((res) => {
        setMenuRequestStatus('success')
        setMenuData(res.data)
        if (isKiosk) {
          menuLoadSucceeded(res.data?.menus, res.data?.stands)
        }
        setCurrentMenu(res.data?.menus)
      })
      .catch((err) => {
        // if failed due to invalid token, add a small wait to allow
        // token to refresh before re-attempting a fetch.
        if (err?.response?.status === 401) {
          setTimeout(() => {
            setMenuRequestStatus('failed')
          }, 1000)
        } else {
          setMenuRequestStatus('failed')
        }
      })
  }

  useEffect(() => {
    onMount(fromSelfCheckoutConfig, fromPrinterSettings, fromBarcodeScanningConfig)

    // stop requests when offline
    if (!networkAvailable) return

    loadPrinters()
    refreshPusherInformation()
  }, [fromSelfCheckoutConfig, fromPrinterSettings, fromBarcodeScanningConfig])

  // To ensure that the correct menus are fetched, wait until the device's build info is ready and available.
  useEffect(() => {
    const isApk = isRunningOnAndroid()
    if (hasBuildInfo || !isApk) {
      fetchMenus()
    }
  }, [hasBuildInfo])

  useEffect(() => {
    if (networkAvailable) {
      if (menuRequestStatus === 'failed') fetchMenus()
      if (printerRequestStatus === 'failed') loadPrinters()
    }
  }, [networkAvailable, menuRequestStatus, printerRequestStatus])

  useEffect(() => {
    if (submitted && !loading && !failed && attendantsFetchCompleted) {
      handleNavigation()
    }
  }, [submitted, loading, failed, history, fetchAttendantsStatus])

  const setCurrentMenu = (menus) => {
    const filteredMenus = menus.filter((menu) => menu?.serviceType?.toLowerCase() === menuTypes[deviceSubtype])
    const menuUuids = filter(map(filteredMenus, 'uuid'), (uuid) => previousSelectedIds.includes(uuid))
    if (!isEmpty(menuUuids)) {
      setSelectedMenuUuids(menuUuids)
    }
  }

  useEffect(() => {
    setSelectedMenuUuids([])
  }, [deviceSubtype])

  useEffect(() => {
    if (!printerSetup && !fromSelfCheckoutConfig && !fromPrinterSettings && !fromBarcodeScanningConfig) return

    setDeviceSubtypeSelected(true)
    setMenuSelected(true)
    setCloseTabMethodSelected(true)
  }, [printerSetup, fromSelfCheckoutConfig, fromPrinterSettings, fromBarcodeScanningConfig])

  const getIcon = (isSelected, forceRadioButtons) => {
    if (forceRadioButtons) {
      return isSelected ? <SelectedRadioButton /> : <UnselectedRadioButton />
    }
    return isSelected ? selectedIcon : unselectedIcon
  }

  const renderRecords = (headerRecords, records, selectedUuids, onClick, forceRadioButtons = false) => {
    return headerRecords.concat(records.sort((a, b) => (a.name > b.name ? 1 : -1))).map((record) => {
      const isSelected = selectedUuids?.includes(record.uuid)
      const icon = getIcon(isSelected, forceRadioButtons)
      const style = { backgroundColor: isSelected ? SHIFT4_BLUE : undefined }

      return (
        <Record
          key={record.uuid}
          icon={scaleSvgIcon(icon)}
          name={record.name}
          style={style}
          styles={styles}
          onClick={() => onClick(record.uuid, isSelected)}
        />
      )
    })
  }

  const renderCloseTabMethods = () => {
    const TIP_ON_DEVICE = 'Tip on Device'
    const TIP_ON_PRINTED_SLIP = 'Tip on Printed Slip'

    return (
      <div>
        <Record
          key={TIP_ON_DEVICE}
          disabled={!tipsEnabled}
          icon={scaleSvgIcon(!backbarMode && tipsEnabled ? selectedIcon : unselectedIcon)}
          name={TIP_ON_DEVICE}
          style={{ backgroundColor: !backbarMode && tipsEnabled ? SHIFT4_BLUE : undefined }}
          styles={styles}
          onClick={() => {
            if (tipsEnabled) setBackbarMode(false)
          }}
        />
        <Record
          key={TIP_ON_PRINTED_SLIP}
          disabled={!tipsEnabled}
          icon={scaleSvgIcon(backbarMode ? selectedIcon : unselectedIcon)}
          name={TIP_ON_PRINTED_SLIP}
          style={{ backgroundColor: backbarMode ? SHIFT4_BLUE : undefined }}
          styles={styles}
          onClick={() => {
            if (tipsEnabled) setBackbarMode(true)
          }}
        />
      </div>
    )
  }

  /**
   * onClick event for renderDeviceTypes
   * @param {string} subtype the new device subtype
   */
  const selectDeviceMode = (selectedSubtype) => {
    if (posDeviceType === POS_DEVICE_TYPES.E700) {
      dispatch(setTipOnCardReader(selectedSubtype === CFD))
      dispatch(setDeviceMode(MODES.UNKNOWN))
      setDeviceSubtype(selectedSubtype === CFD ? CONCESSIONS : selectedSubtype)
      return
    }

    dispatch(setDeviceMode(selectedSubtype === CFD ? MODES.POS : MODES.UNKNOWN))
    setDeviceSubtype(selectedSubtype === CFD ? CONCESSIONS : selectedSubtype)
  }

  const renderDeviceTypes = () => {
    return DEVICE_TYPES.map((subtype) => {
      if (isCFDCapableDevice && subtype === CFD) return null

      // if the device mode is unknown, we want to select the device subtype.
      let isSelected = deviceMode === MODES.UNKNOWN ? deviceSubtype === subtype : subtype === CFD && deviceSubtype === CONCESSIONS

      if (posDeviceType === POS_DEVICE_TYPES.E700) {
        isSelected = (tipOnCardReader && subtype === CFD) || (!tipOnCardReader && deviceSubtype === subtype)
      }

      const style = { backgroundColor: isSelected ? SHIFT4_BLUE : undefined }

      const icon = isSelected ? <SelectedRadioButton /> : <UnselectedRadioButton />

      return (
        <Record
          key={subtype}
          icon={scaleSvgIcon(icon)}
          isSelected={isSelected}
          name={DeviceSubtypes.displayName(subtype)}
          style={style}
          styles={styles}
          onClick={() => selectDeviceMode(subtype)}
        />
      )
    })
  }

  const renderStatus = (requestStatus, groupedItems, itemName) => {
    if (requestStatus === 'loading' && !fromSelfCheckoutConfig && !fromPrinterSettings && !fromBarcodeScanningConfig)
      return <VNLoader />
    if (requestStatus === 'failed') return <p className={styles.status}>An error occurred</p>
    if (requestStatus !== 'loading' && isEmpty(groupedItems))
      return <p className={styles.status}>No POS {itemName} are available!</p>
  }

  const onMenuSelected = () => {
    setMenuSelected(true)

    //if the user can't select backbar mode then we consider it selected and default to false
    if (!canEnableBackbarMode) {
      setBackbarMode(false)
      setCloseTabMethodSelected(true)
      return
    }

    //if a menu was selected and tips are not enabled then we need to default backbar mode to false
    if (!tipsEnabled) {
      setBackbarMode(false)
    }
  }

  const handleDone = () => {
    const isConcessions = DeviceSubtypes.isConcessions(deviceSubtype)
    const isKiosk = DeviceSubtypes.isKiosk(deviceSubtype)
    const selectedNetworkPrinter = selectedNetworkPrinterUuids[0]
    const configParams = {
      menuUuids: selectedMenuUuids,
      deviceSubtype: deviceSubtype,
      deviceMode: deviceMode,
      receiptPrinterUuid: selectedNetworkPrinter,
    }

    if (backbarMode && selectedNetworkPrinter === 'none') {
      setShowNoPrinterModal(true)
      return
    }

    setCurrentNetworkPrinterUuid(selectedNetworkPrinter)
    setCurrentMenuId(selectedMenuUuids[0])

    let selectedMenus
    let selectedStands
    let currentStand

    if (isKiosk) {
      selectedMenus = filter(filteredMenus, (menu) => selectedMenuUuids.includes(menu?.uuid))
      const standMenuUuids = map(selectedMenus, (menu) => menu.standUuid)
      selectedStands = filter(stands, (stand) => standMenuUuids.includes(stand?.uuid))
    } else {
      selectedMenus = filter(filteredMenus, (menu) => menu?.uuid === selectedMenuUuids[0])
      selectedStands = filter(stands, (stand) => stand?.uuid === selectedMenus[0]?.standUuid)
      currentStand = selectedStands?.[0]
    }

    menuLoadSucceeded(selectedMenus, selectedStands)
    hasConfig ? updateConfig(configParams) : createConfig(configParams)

    // Set android to concessions device subtype in order to trigger offline only mode cool down for all pos modes
    executeDeviceSubtype(DeviceSubtypes.concessions)

    if (isConcessions && deviceMode === MODES.POS) {
      const printingEnabled = calculatePrintingEnabled(selectedNetworkPrinter)

      const initializationValues = {
        title: currentStand?.name,
        color: BRANDING_COLOR,
        deviceId: getDeviceId(),
        serialNumber: getDeviceSerial(),
        appConfigurationDeviceId: configurationUuid,
        accessToken: accessToken,
        printingEnabled: printingEnabled,
        textReceiptsEnabled: textReceiptsEnabled,
        enablePosQrCodeReceipts: posQrCodeReceiptsEnabled,
        qrReceiptsUrl: qrReceiptsUrl,
        emailReceiptsEnabled: emailReceiptsEnabled,
        invoiceNumberPrefix: invoiceNumberPrefix(),
        charitySettings: {
          roundUpForCharityEnabled,
          charityOptionsEnabled,
          charityDescription,
          charityOptions,
          charityImage,
          charityName,
        },
        defaultTipEnabled,
      }

      initializationValues.menuImage = selectedMenus?.images?.detail
      initializationValues.image = currentStand?.images?.detail

      sendInitializeCFD(initializationValues)
    }

    //if we have a CFD device paired but user didn't select CFD mode then we need to initialize the CFD with the default values
    if (isConcessions && deviceMode !== MODES.POS) {
      sendInitializeCFD({
        title: 'Please Configure Your POS',
        menuImage: undefined,
        image: undefined,
        color: '#000000',
        accessToken: undefined,
      })
    }

    //backbad mode setup
    if (isConcessions) {
      if (backbarMode) {
        enableBackbarMode()
      } else {
        disableBackbarMode()
      }
    }

    /*
    If we're not in Kiosk or barcode scanning FF is off or the user selected more than one menu then disable the
    Kiosk barcode scanning flag
    */
    if (!canEnableKioskBarcodeScanning) {
      dispatch(disableKioskBarcodeScanning())
    }

    setSubmitted(true)
  }

  const goBack = () => {
    const prevLocation = history?.location?.state?.prevLocation ?? ''
    if (deviceSubtypeSelected && !menuSelected) {
      //pressed back from the select menu step
      setDeviceSubtypeSelected(false)
    } else if (deviceSubtypeSelected && menuSelected && !closeTabMethodSelected) {
      //pressed back from the Select close tab method step
      setMenuSelected(false)
    } else if (deviceSubtypeSelected && menuSelected && closeTabMethodSelected) {
      //pressed back from the Select Receipt Printer step
      //if the user can't select backbar mode then we need to skip the screen we do this by setting menu selected to false
      if (!canEnableBackbarMode) {
        setMenuSelected(false)
      }

      setCloseTabMethodSelected(false)
    } else if (prevLocation === '/kiosk/order/new') {
      history.push('/kiosk/attract_loop')
    } else {
      history.goBack()
    }
  }

  const onSelectMenuConcessions = (menuUuid) => {
    setSelectedMenuUuids([menuUuid])
  }

  const onSelectMenuKiosk = (selectedMenuUuid, isSelected) => {
    const menuUuids = isSelected
      ? selectedMenuUuids.filter((id) => id !== selectedMenuUuid)
      : [...selectedMenuUuids, selectedMenuUuid]
    setSelectedMenuUuids(menuUuids)
  }

  const displayHeader = document?.body?.clientWidth >= 1000

  const onSelectMenuItem = deviceSubtype === KIOSK ? onSelectMenuKiosk : onSelectMenuConcessions

  const noPrinterModal = (
    <ConfirmModalV2
      headerText={'Printer Selection Required'}
      onButtonTwoClick={() => setShowNoPrinterModal(false)}
      subtext={'You must select a printer when “Tip on Printed Slip" is enabled'}
      singleButtonOnly={true}
      buttonTwoText={'OKAY'}
    />
  )

  return (
    <div>
      {showNoPrinterModal && noPrinterModal}
      <div className={classNames}>
        <VNConcessionsNavBar textDisplay={isMobileViewPort && menuHeader} useSmallTitleFont={true} useHamburgerIcon={true} />

        {displayHeader && (
          <>
            <div className={styles.header}>
              {menuHeader}
              {getNoTipsMessage()}
            </div>
          </>
        )}

        {renderStatus(menuRequestStatus, groupedMenus, 'menus')}
        <div className={styles.container}>
          {!deviceSubtypeSelected && <div className={styles.standMenus}>{renderDeviceTypes()}</div>}

          {deviceSubtypeSelected && !menuSelected && (
            <div className={styles.standMenus}>
              {Object.keys(groupedMenus).map((standUuid) => {
                const stand = stands.find((stand) => stand.uuid === standUuid)
                const menus = groupedMenus[standUuid]
                return (
                  <div className={styles.menuSelection} key={standUuid}>
                    <div className={styles.standName}>{stand?.name}</div>
                    {renderRecords([], menus, selectedMenuUuids, onSelectMenuItem)}
                  </div>
                )
              })}
            </div>
          )}

          {deviceSubtypeSelected && menuSelected && !closeTabMethodSelected && (
            <>
              <div className={styles.container}>
                {!displayHeader && getNoTipsMessage()}
                <div className={styles.standMenus}>{renderCloseTabMethods()}</div>
              </div>
            </>
          )}

          {closeTabMethodSelected && (
            <div className={styles.container}>
              {renderStatus(printerRequestStatus, networkPrinters, 'printers')}
              <div className={styles.standMenus}>
                {renderRecords(
                  defaultPrinterRecords,
                  deviceSubtype !== KIOSK ? networkPrinters : [],
                  selectedNetworkPrinterUuids,
                  onSelectNetworkPrinter,
                  true,
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.buttonContainer}>
          {!deviceSubtypeSelected && (
            <Button
              useMui
              disabled={loading || !deviceSubtype}
              className={styles.nextButton}
              onClick={() => setDeviceSubtypeSelected(true)}
            >
              {loading ? 'Loading...' : 'Next'}
            </Button>
          )}

          {deviceSubtypeSelected && !menuSelected && (
            <>
              <Button useMui disabled={loading} className={styles.backButton} onClick={goBack}>
                Back
              </Button>
              <Button
                useMui
                disabled={loading || isEmpty(selectedMenuUuids)}
                className={styles.doneButton}
                onClick={() => {
                  onMenuSelected()
                }}
              >
                {loading ? 'Loading...' : 'Next'}
              </Button>
            </>
          )}

          {menuSelected && !closeTabMethodSelected && deviceSubtype !== KIOSK && (
            <>
              <Button useMui disabled={loading} className={styles.backButton} onClick={goBack}>
                Back
              </Button>
              <Button useMui disabled={loading} className={styles.doneButton} onClick={() => setCloseTabMethodSelected(true)}>
                {loading ? 'Loading...' : 'Next'}
              </Button>
            </>
          )}
          {closeTabMethodSelected && (
            <>
              <Button useMui disabled={loading || fromPrintingConfigLoading} className={styles.backButton} onClick={goBack}>
                Back
              </Button>
              <Button
                useMui
                disabled={loading || isEmpty(selectedNetworkPrinterUuids) || fromPrintingConfigLoading}
                className={styles.doneButton}
                onClick={() => handleDone()}
              >
                {loading ? 'Loading...' : doneButtonText}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  const currentDeviceSubtype = getDeviceSubtype(state)
  const failed = get(state, 'config.remote.failed', false)
  const hasConfig = get(state, 'config.hasConfig', false)
  const loading = get(state, 'config.remote.loading', false)
  const attendantsExist = !isEmpty(getAttendants(state))
  const fetchAttendantsStatus = getFetchAttendantsStatus(state)
  const isKiosk = getIsKiosk(state)
  const paymentType = getPaymentType(state)
  const accessToken = getAccessToken()
  const configurationUuid = getCurrentConfiguration(state)?.uuid
  const previousSelectedIds = getSelectedMenusIds(state)
  const selectedNetworkPrinter = getNetworkPrinter(state)
  const hasBuildInfo = getBuildInfoReceived(state)
  const networkPrinters = getNetworkPrinters(state)
  const printerRequestStatus = getPrinterRequestStatus(state)
  const printerSetup = ownProps?.history?.location?.state?.printerSetup || false
  const fromSelfCheckoutConfig = ownProps?.history?.location?.state?.fromSelfCheckoutConfig || false
  const fromPrinterSettings = ownProps?.history?.location?.state?.fromPrinterSettings || false
  const fromBarcodeScanningConfig = ownProps?.history?.location?.state?.fromBarcodeScanningConfig || false

  return {
    attendantsExist,
    currentDeviceSubtype,
    currentNetworkPrinters: selectedNetworkPrinter ? [selectedNetworkPrinter] : [],
    failed,
    hasConfig,
    loading,
    fetchAttendantsStatus,
    isKiosk,
    paymentType,
    accessToken,
    configurationUuid,
    previousSelectedIds,
    hasBuildInfo,
    networkPrinters,
    printerRequestStatus,
    printerSetup,
    fromSelfCheckoutConfig,
    fromPrinterSettings,
    fromBarcodeScanningConfig,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const replace = get(ownProps, 'history.replace', () => {})

  return {
    createConfig: (payload) => dispatch(createConfig(payload)),
    updateConfig: (payload) => dispatch(updateConfig(payload)),
    setCurrentNetworkPrinterUuid: (uuid) => dispatch(currentNetworkPrinterSelected(uuid)),
    setCurrentMenuId: (menuId) => dispatch(currentMenuSelected(menuId)),
    setPusherKey: (key) => dispatch(setKey(key)),
    setPusherCluster: (cluster) => dispatch(setCluster(cluster)),
    loadPrinters: () => {
      dispatch(fetchPrinters())
    },
    menuLoadSucceeded: (menus, stands) => dispatch(menusSucceeded({ data: { stands, menus } })),
    setSelectedMenu: (menu, stand) => dispatch(menusSucceeded({ data: { stands: [stand], menus: [menu] } })),
    redirectToPin: (fetchAttendantsStatus) =>
      replace({
        pathname: '/user-login',
        state: {
          attendantsAlreadyFetched: fetchAttendantsStatus === 'succeeded',
        },
      }),
    onMount: (fromSelfCheckoutConfig, fromPrinterSettings, fromBarcodeScanningConfig) => {
      dispatch(resetBaseLoad())
      dispatch(clearPromotionsOnOrder())
      dispatch(clearCart())
      dispatch(fetchAttendants())

      if (fromPrinterSettings) return
      if (fromSelfCheckoutConfig) return
      if (fromBarcodeScanningConfig) return
    },
    enableBackbarMode: () => dispatch(enableBackbarMode()),
    disableBackbarMode: () => dispatch(disableBackbarMode()),
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RevenueCenterSetup))
